/**
 * `/super` view state that belongs in the URL.
 *
 * Same reasoning as `admin-search.ts`: a reload should land where you were,
 * and "look at the halftime card at 4K" should be a link you can send.
 *
 * Lives in its own module because `validateSearch` runs on the ROUTE, which is
 * in the main chunk. Importing it from `SuperPage` would drag the whole lazy
 * /super bundle — the one an organiser must never download (AUTH.md §6) — into
 * every OBS Browser Source and every QR-code landing.
 */

export const SUPER_VIEWS = [
  "overview",
  "live",
  "analytics",
  "console",
  "accounts",
  "tournaments",
  "clubs",
  "funnel",
  "log",
  "overlay",
] as const;
export type SuperView = (typeof SUPER_VIEWS)[number];

export const DEFAULT_SUPER_VIEW: SuperView = "overview";

export interface SuperSearch {
  view: SuperView;
  record?: string;
  q?: string;
  filter?: string;
  kind?: string;
  page?: number;
  days?: number;
  trafficDays?: number;
}

/** An unknown view falls back to the console rather than rendering nothing: a
 *  hand-edited or stale link must never produce a blank platform surface. */
export function parseSuperSearch(search: Record<string, unknown>): SuperSearch {
  const v = search["view"];
  const text = (key: string, max: number) =>
    typeof search[key] === "string" ? (search[key] as string).slice(0, max) : "";
  const page = Number(search["page"]);
  const days = Number(search["days"]);
  const trafficDays = Number(search["trafficDays"]);
  return {
    ...([1, 7, 30].includes(trafficDays) ? { trafficDays } : {}),
    ...(text("record", 100) ? { record: text("record", 100) } : {}),
    ...(text("q", 200) ? { q: text("q", 200) } : {}),
    ...(text("filter", 40) ? { filter: text("filter", 40) } : {}),
    ...(text("kind", 40) ? { kind: text("kind", 40) } : {}),
    ...(Number.isInteger(page) && page > 1 && page < 100000 ? { page } : {}),
    ...([1, 7, 30, 90, 365].includes(days) ? { days } : {}),
    view:
      typeof v === "string" && (SUPER_VIEWS as readonly string[]).includes(v)
        ? (v as SuperView)
        : DEFAULT_SUPER_VIEW,
  };
}
